import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import {
  Mail, Search, Download, Users, TrendingUp, Ticket,
  DollarSign, Loader2, ChevronLeft, ChevronRight, Filter
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const NewsletterManagement = () => {
  const [subscribers, setSubscribers] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [exporting, setExporting] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [subsRes, statsRes] = await Promise.all([
        axios.get(`${API}/admin/newsletter/subscribers`, {
          params: { search: search || undefined, status: statusFilter, page, limit: 30 },
        }),
        axios.get(`${API}/admin/newsletter/stats`),
      ]);
      setSubscribers(subsRes.data.subscribers || []);
      setTotal(subsRes.data.total || 0);
      setTotalPages(subsRes.data.pages || 1);
      setStats(statsRes.data);
    } catch (err) {
      console.error('Failed to fetch newsletter data:', err);
    } finally { setLoading(false); }
  }, [search, statusFilter, page]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await axios.get(`${API}/admin/newsletter/export`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `lumera_subscribers_${new Date().toISOString().slice(0,10)}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('CSV exported');
    } catch (err) {
      toast.error('Export failed');
    } finally { setExporting(false); }
  };

  return (
    <div data-testid="newsletter-management">
      {/* KPI Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <StatCard icon={Users} label="Subscribers" value={stats.subscribed} sub={`${stats.total} total`} color="text-blue-600" bg="bg-blue-50" />
          <StatCard icon={TrendingUp} label="Last 30 days" value={`+${stats.new_last_30_days}`} sub={`${stats.unsubscribe_rate}% unsubscribed`} color="text-emerald-600" bg="bg-emerald-50" />
          <StatCard icon={Ticket} label="Coupons used" value={stats.coupon_redemptions} sub={`${stats.redemption_rate}% rate`} color="text-amber-600" bg="bg-amber-50" />
          <StatCard icon={DollarSign} label="Coupon revenue" value={`$${stats.total_revenue}`} sub="from welcome coupons" color="text-violet-600" bg="bg-violet-50" />
        </div>
      )}

      {/* Sources breakdown */}
      {stats?.sources?.length > 0 && (
        <div className="mb-6 bg-stone-50 rounded-xl p-4 border border-stone-100">
          <h4 className="text-xs font-sans font-bold text-stone-600 mb-2">Traffic sources</h4>
          <div className="flex flex-wrap gap-2">
            {stats.sources.map(s => (
              <span key={s.source} className="text-[10px] font-sans bg-white px-2.5 py-1 rounded-full border border-stone-100 text-stone-600">
                {s.source}: <strong>{s.count}</strong>
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 mb-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
          <input
            data-testid="newsletter-search"
            type="text"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            placeholder="Search by email..."
            className="w-full pl-10 pr-4 py-2.5 bg-white border border-stone-200 rounded-xl text-xs font-sans focus:outline-none focus:border-[#B49A88]"
          />
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1 bg-white border border-stone-200 rounded-xl px-1 py-1">
            {['all', 'subscribed', 'unsubscribed'].map(s => (
              <button
                key={s}
                data-testid={`newsletter-filter-${s}`}
                onClick={() => { setStatusFilter(s); setPage(1); }}
                className={`px-3 py-1.5 rounded-lg text-[10px] font-sans font-semibold transition-colors ${
                  statusFilter === s ? 'bg-stone-900 text-white' : 'text-stone-500 hover:text-stone-700'
                }`}
              >
                {s === 'all' ? 'All' : s === 'subscribed' ? 'Active' : 'Unsubscribed'}
              </button>
            ))}
          </div>
          <Button
            data-testid="newsletter-export-csv"
            variant="outline"
            size="sm"
            onClick={handleExport}
            disabled={exporting}
            className="h-9 text-xs font-sans"
          >
            {exporting ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Download className="w-3 h-3 mr-1" />}
            CSV
          </Button>
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-stone-400" />
        </div>
      ) : subscribers.length === 0 ? (
        <div className="text-center py-12">
          <Mail className="w-10 h-10 mx-auto text-stone-300 mb-3" />
          <p className="text-sm font-sans text-stone-400">No subscribers yet</p>
        </div>
      ) : (
        <>
          <div className="border border-stone-100 rounded-xl overflow-hidden bg-white">
            <div className="overflow-x-auto">
              <table className="w-full text-xs font-sans">
                <thead>
                  <tr className="bg-stone-50">
                    <th className="text-left px-4 py-3 font-semibold text-stone-500">Email</th>
                    <th className="text-left px-4 py-3 font-semibold text-stone-500">Status</th>
                    <th className="text-left px-4 py-3 font-semibold text-stone-500">Source</th>
                    <th className="text-left px-4 py-3 font-semibold text-stone-500">Coupon</th>
                    <th className="text-left px-4 py-3 font-semibold text-stone-500">Redeemed</th>
                    <th className="text-left px-4 py-3 font-semibold text-stone-500">Revenue</th>
                    <th className="text-left px-4 py-3 font-semibold text-stone-500">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {subscribers.map((sub, i) => (
                    <tr key={sub.email} className={`border-t border-stone-50 ${i % 2 === 0 ? '' : 'bg-stone-50/30'}`}>
                      <td className="px-4 py-3 text-stone-800 font-medium">{sub.email}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider ${
                          sub.status === 'subscribed'
                            ? 'bg-emerald-50 text-emerald-600'
                            : 'bg-stone-100 text-stone-400'
                        }`}>
                          {sub.status === 'subscribed' ? 'Active' : 'Unsubscribed'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-stone-500">
                        {sub.utm_source || sub.source || 'direct'}
                        {sub.utm_campaign && <span className="text-stone-300 ml-1">/ {sub.utm_campaign}</span>}
                      </td>
                      <td className="px-4 py-3">
                        <code className="text-[10px] bg-stone-50 px-1.5 py-0.5 rounded text-stone-600">{sub.coupon_code}</code>
                      </td>
                      <td className="px-4 py-3">
                        {sub.coupon_redeemed
                          ? <span className="text-emerald-600 font-semibold">Yes</span>
                          : <span className="text-stone-300">No</span>}
                      </td>
                      <td className="px-4 py-3 text-stone-600">
                        {sub.coupon_revenue > 0 ? `$${sub.coupon_revenue}` : '—'}
                      </td>
                      <td className="px-4 py-3 text-stone-400 whitespace-nowrap">
                        {sub.created_at ? new Date(sub.created_at).toLocaleDateString('en-GB') : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-4">
              <p className="text-[10px] font-sans text-stone-400">{total} subscribers total</p>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="p-1.5 rounded-lg border border-stone-200 text-stone-400 hover:text-stone-600 disabled:opacity-30"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <span className="text-xs font-sans text-stone-500">{page} / {totalPages}</span>
                <button
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="p-1.5 rounded-lg border border-stone-200 text-stone-400 hover:text-stone-600 disabled:opacity-30"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

const StatCard = ({ icon: Icon, label, value, sub, color, bg }) => (
  <div className="bg-white border border-stone-100 rounded-xl p-4">
    <div className="flex items-center gap-2 mb-2">
      <div className={`w-7 h-7 rounded-lg ${bg} flex items-center justify-center`}>
        <Icon className={`w-3.5 h-3.5 ${color}`} />
      </div>
      <span className="text-[10px] font-sans font-semibold text-stone-400 uppercase tracking-wider">{label}</span>
    </div>
    <p className="text-xl font-serif font-bold text-stone-800">{value}</p>
    <p className="text-[10px] font-sans text-stone-400 mt-0.5">{sub}</p>
  </div>
);
